import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { useNativeCamera } from '../hooks/useNativeCamera';
import { scrollFieldIntoView } from '../utils/scrollFieldIntoView';
import ModalCloseButton from './ModalCloseButton';
import '../styles/components/inventory.css';

interface ImageSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemName: string;
  searchImages: string[];
  initials: string;
  color1: string;
  color2: string;
  currentImageId?: number | null;
  imageSourceType?: string;
  onSelectSearchImage: (imageUrl: string) => void;
  onSelectInitials: () => void;
  onTakePhoto: (file: File) => void;
  onSearchKeywords?: (keywords: string) => void;
  isLoading?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
}

const ImageSelectionModal: React.FC<ImageSelectionModalProps> = ({
  isOpen,
  onClose,
  itemName,
  searchImages,
  initials,
  color1,
  color2,
  currentImageId,
  imageSourceType,
  onSelectSearchImage,
  onSelectInitials,
  onTakePhoto,
  onSearchKeywords,
  isLoading = false,
  onLoadMore,
  isLoadingMore = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { pickPhoto, isNative } = useNativeCamera();
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [pendingAction, setPendingAction] = React.useState<'search_image' | 'initials' | null>(null);
  const [pendingSearchImageUrl, setPendingSearchImageUrl] = React.useState<string | null>(null);
  const [canClose, setCanClose] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number>(-1);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [searchKeywords, setSearchKeywords] = useState<string>('');
  const [showSearchInput, setShowSearchInput] = useState<boolean>(false)

  // Prevent immediate close after opening
  useEffect(() => {
    if (isOpen) {
      setCanClose(false);
      // Simple fixed delay - works for both user and guest mode
      const timer = setTimeout(() => setCanClose(true), 2000);
      return () => clearTimeout(timer);
    } else {
      // Reset when modal closes
      setCanClose(false);
      setSearchKeywords('');
      setShowSearchInput(false);
    }
  }, [isOpen]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchKeywords.trim() && onSearchKeywords) {
      onSearchKeywords(searchKeywords.trim());
    }
  };

  const handleToggleSearch = () => {
    setShowSearchInput(!showSearchInput);
    if (showSearchInput) {
      setSearchKeywords('');
    }
  };

  if (!isOpen) return null;

  const isCurrentUserUpload = imageSourceType === 'user_upload';

  const handleImageClick = (imageUrl: string, index: number) => {
    // Show preview
    setPreviewImage(imageUrl);
    setPreviewIndex(index);
  };

  const handleSelectFromPreview = () => {
    if (!previewImage) return;
    
    // If user has uploaded their own image, ask for confirmation before replacing
    if (currentImageId && isCurrentUserUpload) {
      setPendingSearchImageUrl(previewImage);
      setPendingAction('search_image');
      setShowConfirmModal(true);
      setPreviewImage(null);
      return;
    }
    
    // No user image, just select the search image
    onSelectSearchImage(previewImage);
    onClose();
  };

  const handleClosePreview = () => {
    setPreviewImage(null);
    setPreviewIndex(-1);
  };

  const handlePrevImage = () => {
    if (previewIndex > 0) {
      const newIndex = previewIndex - 1;
      setPreviewIndex(newIndex);
      setPreviewImage(searchImages[newIndex]);
    }
  };

  const handleNextImage = () => {
    if (previewIndex < searchImages.length - 1) {
      const newIndex = previewIndex + 1;
      setPreviewIndex(newIndex);
      setPreviewImage(searchImages[newIndex]);
    }
  };

  // Swipe handling
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      handleNextImage();
    } else if (isRightSwipe) {
      handlePrevImage();
    }
  };

  const handleUseInitials = () => {
    // If user has uploaded their own image, ask for confirmation before removing
    if (currentImageId && isCurrentUserUpload) {
      setPendingAction('initials');
      setShowConfirmModal(true);
      return;
    }
    
    // No uploaded image, just switch to initials
    onSelectInitials();
    onClose();
  };

  const handleTakePhoto = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handlePickFromLibrary = async () => {
    if (!isNative) {
      // Fall back to file input on web
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
      return;
    }

    const base64String = await pickPhoto();
    if (base64String) {
      // Convert base64 to File object
      const byteString = atob(base64String);
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const uint8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([uint8Array], { type: 'image/jpeg' });
      const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
      
      onTakePhoto(file);
      onClose();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onTakePhoto(file);
      onClose();
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleConfirmReplace = () => {
    if (pendingAction === 'search_image' && pendingSearchImageUrl) {
      onSelectSearchImage(pendingSearchImageUrl);
    } else if (pendingAction === 'initials') {
      onSelectInitials();
    }
    setShowConfirmModal(false);
    setPendingAction(null);
    setPendingSearchImageUrl(null);
    onClose();
  };

  const handleCancelReplace = () => {
    setShowConfirmModal(false);
    setPendingAction(null);
    setPendingSearchImageUrl(null);
  };

  return createPortal(
    <>
      {/* Main selection modal */}
      <div 
        className="image-selection-modal-overlay" 
        onClick={() => canClose && onClose()}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div 
          className="image-selection-modal" 
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div className="image-selection-header">
            <h3>Select Image for "{itemName}"</h3>
            <ModalCloseButton onClick={onClose} />
          </div>
          
          {/* Search keywords input */}
          {onSearchKeywords && (
            <div style={{ padding: '0 16px 12px 16px' }}>
              {!showSearchInput ? (
                <button
                  type="button"
                  onClick={handleToggleSearch}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid var(--color-border-strong)',
                    borderRadius: '6px',
                    background: 'var(--color-bg-primary)',
                    color: 'var(--color-text-muted)',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <span>🔍</span>
                  <span>Search different keywords...</span>
                </button>
              ) : (
                <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={searchKeywords}
                    onChange={(e) => setSearchKeywords(e.target.value)}
                    placeholder={itemName}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid var(--color-border-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    onFocus={(e) => { e.target.style.borderColor = 'var(--color-accent-secondary)'; scrollFieldIntoView(e.target); }}
                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border-strong)'}
                  />
                  <button
                    type="submit"
                    disabled={!searchKeywords.trim()}
                    style={{
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '6px',
                      background: searchKeywords.trim() ? 'var(--color-accent-secondary)' : 'var(--color-border-strong)',
                      color: 'var(--color-text-inverse)',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: searchKeywords.trim() ? 'pointer' : 'not-allowed',
                    }}
                  >
                    Search
                  </button>
                </form>
              )}
            </div>
          )}
          
          {/* Action buttons - always visible at top */}
          <div className="image-selection-actions">
            <button type="button" className="image-action-btn" onClick={handleTakePhoto}>
              <span className="action-icon">📷</span>
              <span className="action-label">Take Photo</span>
            </button>
            <button type="button" className="image-action-btn" onClick={handlePickFromLibrary}>
              <span className="action-icon">🖼️</span>
              <span className="action-label">Photo Library</span>
            </button>
            <button type="button" className="image-action-btn" onClick={handleUseInitials}>
              <div 
                className="action-icon initials-icon"
                style={{
                  background: `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`,
                }}
              >
                {initials}
              </div>
              <span className="action-label">Use Initials</span>
            </button>
          </div>
          
          <div className="image-selection-content">
            {/* Always show grid layout */}
            <div className="image-selection-grid">
              {isLoading ? (
                // Show placeholder squares while loading (hide stale images)
                // 12 placeholder squares (4 rows of 3)
                Array.from({ length: 12 }).map((_, index) => (
                  <div
                    key={`placeholder-${index}`}
                    className="image-option image-placeholder"
                    style={{ 
                      background: 'linear-gradient(135deg, var(--color-bg-tertiary) 0%, var(--color-border) 100%)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <div className="placeholder-shimmer" />
                  </div>
                ))
              ) : searchImages.length > 0 ? (
                // Show search image options
                searchImages.map((url, index) => {
                  if (!url || typeof url !== 'string') {
                    console.warn('Invalid image URL at index', index, ':', url);
                    return null;
                  }
                  return (
                    <div
                      key={`img-${index}-${url.substring(0, 20)}`}
                      className="image-option"
                      onClick={() => handleImageClick(url, index)}
                    >
                      <img 
                        src={url} 
                        alt={`Option ${index + 1}`}
                        loading="lazy"
                        onError={(e) => {
                          console.error('Failed to load image:', url);
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  );
                }).filter(Boolean)
              ) : (
                // Empty state: show 12 static placeholder cells so the grid
                // takes up space and the keyword input stays above the keyboard
                Array.from({ length: 12 }).map((_, index) => (
                  <div
                    key={`empty-${index}`}
                    className="image-option image-placeholder"
                    style={{ 
                      background: 'var(--color-bg-tertiary)',
                    }}
                  />
                ))
              )}
            </div>
            {/* Show More button — only rendered when onLoadMore is provided */}
            {onLoadMore && !isLoading && searchImages.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
                <button
                  type="button"
                  onClick={onLoadMore}
                  disabled={isLoadingMore}
                  style={{
                    padding: '10px 32px',
                    border: '1px solid var(--color-border-strong)',
                    borderRadius: '8px',
                    background: 'var(--color-bg-primary)',
                    color: isLoadingMore ? 'var(--color-text-faint)' : 'var(--color-text-secondary)',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: isLoadingMore ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isLoadingMore ? 'Loading...' : 'Show More'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Confirmation modal for replacing existing image */}
      {showConfirmModal && (
        <div className="image-selection-modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="image-selection-modal image-selection-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="image-selection-header">
              <h3>Replace Existing Image?</h3>
            </div>
            <div className="image-selection-content">
              <p style={{ marginBottom: '20px', color: 'var(--color-text-muted)' }}>
                This item already has an image. Do you want to replace it?
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button 
                  onClick={() => setShowConfirmModal(false)}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid var(--color-border-strong)',
                    borderRadius: '6px',
                    background: 'var(--color-bg-primary)',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleConfirmReplace}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    background: 'var(--color-accent-secondary)',
                    color: 'var(--color-text-inverse)',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Replace
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      
      {/* Image Preview Overlay */}
      {previewImage && (
        <div className="image-preview-overlay" onClick={handleClosePreview}>
          <div className="image-preview-container" onClick={(e) => e.stopPropagation()}>
            {/* Preview image with swipe */}
            <div 
              className="preview-image-wrapper"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <img src={previewImage} alt="Preview" className="preview-image" />
            </div>
            
            {/* Fixed bottom controls */}
            <div className="preview-controls">
              {/* Navigation bar */}
              <div className="preview-nav-bar">
                <button 
                  className="preview-nav-arrow"
                  onClick={handlePrevImage}
                  disabled={previewIndex === 0}
                >
                  ‹
                </button>
                <div className="preview-counter">
                  {previewIndex + 1} / {searchImages.length}
                </div>
                <button 
                  className="preview-nav-arrow"
                  onClick={handleNextImage}
                  disabled={previewIndex === searchImages.length - 1}
                >
                  ›
                </button>
              </div>
              
              {/* Action buttons */}
              <div className="preview-action-buttons">
                <button className="preview-select-btn" onClick={handleSelectFromPreview}>
                  OK
                </button>
                <button className="preview-cancel-btn" onClick={handleClosePreview}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default ImageSelectionModal;
